// @flow
import type { AbstractComponent, Element, MixedElement } from 'react';
import React from 'react';
import { Field, FieldArray } from 'redux-form';
import type { OptionsProp, State } from './support/types/options.js';
import RenderInput from './RenderInput/index.jsx';
import { getOptionElements, getSelectedOptionText } from './support/options.jsx';
import CheckboxGroup from '#components/support/forms/CheckboxGroup/index.jsx';
import AddressTypeahead from '#components/support/forms/AddressTypeahead/index.js';

type Props = {
  form: string,
  name: string,
  item: string,
  options?: OptionsProp | null,
  readOnly?: boolean,
  readOnlyComponent?: AbstractComponent<{}> | string | null,
  render?: Function,
  state: State,
  type: string | Function,
  value: any,
  format: Function,
  normalize: Function,
  inputClassName?: string | Function | null,
  autofilled?: boolean,
  valuesAreExcluded?: boolean,
  regionCode?: string | Function | null,
};

const BaseField = ({
  form,
  name,
  item,
  options = null,
  readOnly = false,
  readOnlyComponent = null,
  render = null,
  state,
  type,
  value,
  format,
  normalize,
  inputClassName = null,
  // New props for autofilled support with safe defaults
  autofilled = false,
  valuesAreExcluded = false,
  regionCode = null,
}: Props): Element<typeof Field | typeof FieldArray | AbstractComponent<{}>> | MixedElement => {
  // Resolve dynamic type (function-based, similar to options and label)
  const resolvedType = typeof type === 'function' && state && state.values ? type(state) : type;
  const resolvedInputClassName =
    typeof inputClassName === 'function' && state && state.values
      ? inputClassName(state)
      : inputClassName;
  // Disable field if autofilled (similar to current model form behavior)
  const shouldBeReadOnly = readOnly || autofilled;
  if (resolvedType === 'FieldArray' && render) {
    return (
      <FieldArray
        item={item}
        component={render}
        form={form}
        id={name}
        name={name}
        readOnly={shouldBeReadOnly}
      />
    );
  }

  if (readOnly && readOnlyComponent) {
    const displayValue =
      resolvedType === 'select' && options ? getSelectedOptionText(options, value, state) : value;

    return React.createElement(
      readOnlyComponent,
      { id: name },
      typeof format === 'function' ? format(displayValue) : displayValue
    );
  }

  if (resolvedType === 'addressTypeahead') {
    const resolvedRegionCode =
      typeof regionCode === 'function' && state && state.values ? regionCode(state) : regionCode;
    return (
      <Field
        id={name}
        item={item}
        name={name}
        component={AddressTypeahead}
        regionCode={resolvedRegionCode}
        readOnly={readOnly}
        disabled={autofilled}
        inputClassName={resolvedInputClassName}
        format={format}
        normalize={normalize}
      />
    );
  }

  if (resolvedType === 'checkboxGroup') {
    return (
      <Field
        id={name}
        item={item}
        name={name}
        component={CheckboxGroup}
        options={options || []}
        valuesAreExcluded={valuesAreExcluded}
        disabled={shouldBeReadOnly}
        format={format}
        normalize={normalize}
      />
    );
  }

  if (resolvedType === 'select' && options) {
    const optionElements = getOptionElements(options, state);
    return (
      <Field
        id={name}
        item={item}
        name={name}
        type={resolvedType}
        component={RenderInput}
        disabled={shouldBeReadOnly}
        inputClassName={resolvedInputClassName}
        format={format}
        normalize={normalize}
      >
        {optionElements}
      </Field>
    );
  }

  return (
    <Field
      id={name}
      item={item}
      name={name}
      type={resolvedType}
      component={RenderInput}
      readOnly={readOnly}
      disabled={autofilled}
      inputClassName={resolvedInputClassName}
      format={format}
      normalize={normalize}
    />
  );
};

export default BaseField;
